/**
 * MuaqqibDirectoryAdminPage — STAS-only directory of muaqqibs (legal reps).
 * Replaces the older "singleton muaqqib profile" page. Lets STAS:
 *   • add / edit / activate / deactivate muaqqibs
 *   • upload each muaqqib's ID image (used by the client for Najiz PoA)
 *   • see how many records there are
 *
 * Assignment of a muaqqib to a specific application happens from the
 * application detail page (any internal user can assign).
 */
import { useEffect, useState } from "react";
import { Loader2, Trash2, Upload, UserPlus, Edit2, Save, X, ImageIcon } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";

const EMPTY = { full_name: "", national_id: "", date_of_birth: "", phone: "", notes: "", active: true };

export default function MuaqqibDirectoryAdminPage() {
  const { theme } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // muaqqib id being edited (or "new")
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/muaqqibs");
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (m) => {
    setForm({ full_name: m.full_name, national_id: m.national_id, date_of_birth: m.date_of_birth || "", phone: m.phone, notes: m.notes || "", active: m.active });
    setEditing(m.id);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); setErr(""); };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (editing === "new") {
        await api.post("/admin/muaqqibs", form);
      } else {
        await api.patch(`/admin/muaqqibs/${editing}`, form);
      }
      cancel();
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف المعقّب نهائياً؟")) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/muaqqibs/${id}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadId = async (id, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/muaqqibs/${id}/id-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={css.page(theme)} data-testid="muaqqib-directory-page">
      <header style={{ marginBottom: 18 }}>
        <h1 style={{ ...css.h1, color: theme.gold }}>دليل المعقّبين</h1>
        <p style={{ fontSize: 12, opacity: 0.75, maxWidth: 700, lineHeight: 1.8 }}>
          أضف جميع المعقّبين الذين يعمل معهم المكتب. لكل معاملة عند العميل بند معقّب،
          الإدارة/الموظف يختار المعقّب المسؤول من قائمة المعاملة. بياناته (الاسم، الهوية، الميلاد،
          <strong> رقم الجوال للتواصل </strong>، وصورة هويته) تظهر للعميل تلقائياً لإصدار وكالة في ناجز.
        </p>
        <button onClick={startNew} style={css.btnGold(theme)} data-testid="muaqqib-add-new">
          <UserPlus size={14} strokeWidth={2.4} /> معقّب جديد
        </button>
      </header>

      {err && <div style={css.err}>{err}</div>}
      {loading ? (
        <div style={{ textAlign: "center", padding: 30 }}><Loader2 className="vta-spin" size={20} /></div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {items.length === 0 && <div style={{ textAlign: "center", padding: 30, opacity: 0.6 }}>لا يوجد معقّبون بعد. اضغط "معقّب جديد" لإضافة الأول.</div>}
          {items.map((m) => (
            <div key={m.id} style={css.row(theme, !m.active)} data-testid={`muaqqib-row-${m.id}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <strong style={{ color: theme.gold }}>{m.full_name}</strong>
                  {!m.active && <span style={css.badge("#dc2626")}>غير نشط</span>}
                  {m.id_image_present ? (
                    <span style={css.badge("#16a34a")}>✓ هوية</span>
                  ) : (
                    <span style={css.badge("#f59e0b")}>⚠ بلا هوية</span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, opacity: 0.8 }}>
                  <span dir="ltr">ID: {m.national_id}</span>
                  <span dir="ltr">📞 {m.phone}</span>
                  {m.date_of_birth && <span dir="ltr">🎂 {m.date_of_birth}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {m.id_image_present && (
                  <a href={`${API_BASE}/admin/muaqqibs/${m.id}/id-image?t=${Date.now()}`} target="_blank" rel="noopener noreferrer" style={css.iconBtn(theme)} title="عرض الهوية">
                    <ImageIcon size={14} strokeWidth={2.2} />
                  </a>
                )}
                <label style={{ ...css.iconBtn(theme), cursor: "pointer" }} title="رفع/استبدال صورة الهوية">
                  <Upload size={14} strokeWidth={2.2} />
                  <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => uploadId(m.id, e.target.files?.[0])} />
                </label>
                <button onClick={() => startEdit(m)} style={css.iconBtn(theme)} title="تعديل" data-testid={`muaqqib-edit-${m.id}`}><Edit2 size={14} strokeWidth={2.2} /></button>
                <button onClick={() => remove(m.id)} style={{ ...css.iconBtn(theme), color: "#dc2626", borderColor: "#dc2626" }} title="حذف" data-testid={`muaqqib-delete-${m.id}`}><Trash2 size={14} strokeWidth={2.2} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div style={css.modalBack} onClick={cancel}>
          <div style={css.modal(theme)} onClick={(e) => e.stopPropagation()}>
            <div style={css.modalHead(theme)}>
              <strong>{editing === "new" ? "إضافة معقّب" : "تعديل المعقّب"}</strong>
              <button onClick={cancel} style={{ background: "transparent", border: "none", color: theme.textMuted, cursor: "pointer" }}><X size={14} /></button>
            </div>
            <div style={{ padding: 16, display: "grid", gap: 10 }}>
              <Field label="الاسم الكامل" v={form.full_name} on={(v) => setForm((s) => ({ ...s, full_name: v }))} t="muaqqib-name" />
              <Field label="رقم الهوية" v={form.national_id} on={(v) => setForm((s) => ({ ...s, national_id: v }))} ltr t="muaqqib-nid" />
              <Field label="رقم الجوال (سيظهر للعميل للتواصل)" v={form.phone} on={(v) => setForm((s) => ({ ...s, phone: v }))} ltr t="muaqqib-phone" placeholder="+966 5XX XXX XXXX" />
              <Field label="تاريخ الميلاد (YYYY-MM-DD)" v={form.date_of_birth} on={(v) => setForm((s) => ({ ...s, date_of_birth: v }))} ltr t="muaqqib-dob" placeholder="1985-03-15" />
              <Field label="ملاحظات داخلية (لا تظهر للعميل)" v={form.notes} on={(v) => setForm((s) => ({ ...s, notes: v }))} t="muaqqib-notes" />
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
                <input type="checkbox" checked={form.active} onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))} data-testid="muaqqib-active" />
                نشط (يمكن إسناده لمعاملة)
              </label>
              {err && <div style={css.err}>{err}</div>}
              <button onClick={save} disabled={busy} style={css.btnGold(theme)} data-testid="muaqqib-save">
                {busy ? <Loader2 className="vta-spin" size={14} /> : <Save size={14} />} حفظ
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`.vta-spin{animation:vta-spin 1s linear infinite}@keyframes vta-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Field({ label, v, on, ltr, t, placeholder }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, opacity: 0.75, marginBottom: 4 }}>{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "rgba(255,255,255,0.04)", color: "inherit", fontSize: 13, direction: ltr ? "ltr" : "rtl", boxSizing: "border-box", outline: "none" }}
        data-testid={t} />
    </div>
  );
}

const css = {
  page: (theme) => ({ maxWidth: 900, margin: "0 auto", padding: 18, color: theme.text }),
  h1: { fontSize: 22, fontWeight: 900, margin: 0 },
  btnGold: (theme) => ({ background: theme.gold, color: "#0a0a0a", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10 }),
  row: (theme, inactive) => ({ display: "flex", gap: 12, alignItems: "center", padding: 14, background: theme.surface, border: `1px solid ${theme.borderSoft}`, borderRadius: 12, opacity: inactive ? 0.5 : 1 }),
  iconBtn: (theme) => ({ background: "transparent", color: theme.text, border: `1px solid ${theme.borderSoft}`, padding: 6, borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", textDecoration: "none" }),
  badge: (color) => ({ background: `${color}22`, color, padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 800 }),
  err: { background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: 10, borderRadius: 8, fontSize: 12, marginBottom: 12 },
  modalBack: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 9999 },
  modal: (theme) => ({ width: "100%", maxWidth: 480, background: theme.surface, borderRadius: 16, border: `1px solid ${theme.borderSoft}`, overflow: "hidden" }),
  modalHead: (theme) => ({ padding: "12px 16px", borderBottom: `1px solid ${theme.borderSoft}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: theme.surfaceSubtle }),
};
